import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtSigned, downloadCsv } from "@/lib/journal";
import { PaperPlaneTilt, FileCsv } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SyncStatusPanel } from "@/components/SyncStatusPanel";
import { LANGUAGES } from "@/i18n";

export const SettingsDialog = ({ open, onOpenChange }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [dust, setDust] = useState("5");
  const [weekly, setWeekly] = useState(true);
  const [alerts, setAlerts] = useState(true);
  const [alertPct, setAlertPct] = useState("15");
  const [method, setMethod] = useState("average");
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [taxYear, setTaxYear] = useState("");
  const [taxYears, setTaxYears] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then(({ data }) => {
      setEmail(data.report_email || "");
      setDust(String(data.dust_threshold ?? 5));
      setWeekly(data.weekly_digest !== false);
      setAlerts(data.alerts_enabled !== false);
      setAlertPct(String(data.alert_threshold_pct ?? 15));
      setMethod(data.cost_basis_method || "average");
      if (data.language && !localStorage.getItem("tj13_lang")) i18n.changeLanguage(data.language);
    }).catch(() => toast.error(t("settings.loadFailed")));
    api.get("/reports/tax").then(({ data }) => {
      setTaxYears(data.available_years);
      const y = data.available_years[0] || String(new Date().getFullYear());
      setTaxYear(String(y));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !taxYear) return;
    api.get("/reports/tax", { params: { year: taxYear } })
      .then(({ data }) => setTaxSummary(data.summary))
      .catch(() => setTaxSummary(null));
  }, [open, taxYear]);

  const changeLanguage = async (lng) => {
    await i18n.changeLanguage(lng);
    try {
      await api.put("/settings", { language: lng });
    } catch {
      /* language already applied locally */
    }
  };

  const exportTax = async () => {
    try {
      await downloadCsv("/reports/tax/export", { year: taxYear }, `tj13-tax-lots-${taxYear}.csv`);
    } catch {
      toast.error(t("settings.taxExportFailed"));
    }
  };

  const sendAlerts = async () => {
    setSendingAlerts(true);
    try {
      const { data } = await api.post("/alerts/send");
      toast.success(t("settings.alertsSent", { count: data.alerts, to: data.to }));
    } catch (e) {
      toast.info(e?.response?.data?.detail || t("settings.alertsFailed"));
    } finally {
      setSendingAlerts(false);
    }
  };

  const sendWeekly = async () => {
    setSendingWeekly(true);
    try {
      const { data } = await api.post("/reports/weekly/send");
      toast.success(t("settings.weeklySent", { to: data.to }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.weeklyFailed"));
    } finally {
      setSendingWeekly(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        dust_threshold: parseFloat(dust),
        weekly_digest: weekly,
        alerts_enabled: alerts,
        alert_threshold_pct: parseFloat(alertPct),
        cost_basis_method: method,
      };
      if (email.trim()) payload.report_email = email.trim();
      await api.put("/settings", payload);
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || t("settings.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/monthly/send");
      toast.success(t("settings.monthlySent", { period: data.period, to: data.to }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.monthlyFailed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="settings-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            {t("settings.title")} <span className="text-[#C58361]">{t("settings.titleAccent")}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            {t("settings.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("common.language")}</Label>
            <Select value={i18n.language?.startsWith("ru") ? "ru" : "en"} onValueChange={changeLanguage}>
              <SelectTrigger data-testid="settings-language-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`settings-language-${l.id}`}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-mono text-[10px] text-zinc-600">{t("settings.languageHint")}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("settings.reportEmail")}</Label>
            <Input
              data-testid="settings-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
            />
            <p className="font-mono text-[10px] text-zinc-600">
              {t("settings.reportEmailHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("settings.dust")}</Label>
            <Input
              data-testid="settings-dust-input"
              type="number" step="any" min="0" value={dust}
              onChange={(e) => setDust(e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
            <p className="font-mono text-[10px] text-zinc-600">
              {t("settings.dustHint")}
            </p>
          </div>

          <div className="flex items-center justify-between border border-[#1F1F1F] bg-[#050505] px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t("settings.weekly")}</p>
              <p className="mt-1 font-mono text-[10px] text-zinc-600">{t("settings.weeklyHint")}</p>
            </div>
            <Switch
              data-testid="settings-weekly-switch"
              checked={weekly}
              onCheckedChange={setWeekly}
              className="data-[state=checked]:bg-[#C58361]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("settings.method")}</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="settings-method-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                <SelectItem value="average" data-testid="settings-method-average">{t("settings.methodAverage")}</SelectItem>
                <SelectItem value="fifo" data-testid="settings-method-fifo">{t("settings.methodFifo")}</SelectItem>
              </SelectContent>
            </Select>
            <p className="font-mono text-[10px] text-zinc-600">
              {t("settings.methodHint")}
            </p>
          </div>

          <div className="space-y-3 border border-[#1F1F1F] bg-[#050505] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t("settings.alerts")}</p>
                <p className="mt-1 font-mono text-[10px] text-zinc-600">{t("settings.alertsHint")}</p>
              </div>
              <Switch
                data-testid="settings-alerts-switch"
                checked={alerts}
                onCheckedChange={setAlerts}
                className="data-[state=checked]:bg-[#C58361]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Input
                data-testid="settings-alert-pct-input"
                type="number" step="any" min="1" value={alertPct}
                onChange={(e) => setAlertPct(e.target.value)}
                className="h-9 w-24 rounded-none border-[#1F1F1F] bg-[#0A0A0A] font-mono text-xs focus-visible:ring-[#C58361]"
              />
              <span className="font-mono text-[10px] text-zinc-500">{t("settings.alertPctHint")}</span>
              <Button
                data-testid="settings-send-alerts-btn"
                onClick={sendAlerts}
                disabled={sendingAlerts}
                className="ml-auto h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
              >
                {sendingAlerts ? t("settings.checking") : t("settings.checkNow")}
              </Button>
            </div>
          </div>

          <div className="space-y-3 border border-[#1F1F1F] bg-[#050505] p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("settings.taxReport")}</p>
            <div className="flex gap-2">
              <Select value={taxYear} onValueChange={setTaxYear}>
                <SelectTrigger data-testid="settings-tax-year-select" className="h-9 flex-1 rounded-none border-[#1F1F1F] bg-[#0A0A0A] text-xs">
                  <SelectValue placeholder={t("settings.year")} />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {(taxYears.length ? taxYears : [new Date().getFullYear()]).map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`settings-tax-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="settings-tax-export-btn"
                onClick={exportTax}
                className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
              >
                <FileCsv size={14} className="mr-2" /> {t("settings.export")}
              </Button>
            </div>
            {taxSummary && (
              <p className="font-mono text-[10px] text-zinc-500" data-testid="settings-tax-summary">
                {t("settings.taxSummary", {
                  lots: taxSummary.lots,
                  realized: fmtSigned(taxSummary.realized_gain),
                  short: fmtSigned(taxSummary.short_term_gain),
                  long: fmtSigned(taxSummary.long_term_gain),
                })}
              </p>
            )}
          </div>

          <SyncStatusPanel open={open} />

          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="settings-save-btn"
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-none bg-[#C58361] py-5 font-medium text-black hover:bg-[#A86B4C]"
            >
              {saving ? t("common.saving") : t("settings.saveBtn")}
            </Button>
            <Button
              data-testid="settings-send-weekly-btn"
              onClick={sendWeekly}
              disabled={sendingWeekly}
              className="rounded-none border border-[#1F1F1F] bg-transparent px-4 py-5 text-xs text-white hover:bg-[#111111]"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {sendingWeekly ? t("settings.sending") : t("settings.weeklyBtn")}
            </Button>
            <Button
              data-testid="settings-send-report-btn"
              onClick={sendReport}
              disabled={sending}
              className="rounded-none border border-[#1F1F1F] bg-transparent px-4 py-5 text-xs text-white hover:bg-[#111111]"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {sending ? t("settings.sending") : t("settings.monthlyBtn")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
